'use client';

import React from 'react';
import { LinkProps } from '../../types';
import type { MediaType } from '@repo/ui/src/types/image';
import Image from '@repo/ui/src/components/Image';
import { classNames } from '@repo/ui/src/utils/classNames';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { useCurrencyString } from '@repo/ui/src/hooks/useCurrencyString';
import { useTranslation } from '@repo/ui/src/context/Translation/useTranslation';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';

export interface Product {
  id: string;
  title: string;
  price: number;
  maxPrice?: number;
  originalPrice?: number;
  link?: LinkProps;
  isNew?: boolean;
}

interface Props {
  product: Product;
  image?: MediaType;
}

const MeaterProductCard = ({ product, image }: Props) => {
  const { t } = useTranslation();
  const formatPrice = useCurrencyString();

  if (!product) return null;

  const hasPriceRange = product.maxPrice !== undefined && product.maxPrice > product.price;

  return (
    <Anchor
      {...product.link}
      data-test-id={`meater_product_card`}
      className={classNames(
        'relative h-full z-10 bg-white block no-underline',
        // focus
        'focus:outline-none focus:outline-2 focus:ring focus:ring-brand-200',
        // after
        'after:absolute after:-z-20 after:-inset-0 after:transition after:duration-300 after:ease-in-out after:bg-gradient-to-br after:from-white after:via-white after:to-warm-100 after:opacity-0',
        // hover
        'hover:after:opacity-100',
      )}
    >
      <div className="max-w-[420px] m-auto">
        <div className="grid h-full text-left content-end grid-rows-[auto_1fr] px-8 py-8 gap-5">
          <div className="relative flex justify-center -z-10">
            {product.isNew && (
              <div className="absolute top-0 left-0 z-10 px-3 py-1 text-sm font-semibold text-white rounded-md bg-brand-300">
                {t('productCard.new', undefined, { default: 'New' })}
              </div>
            )}
            {image && (
              <Image
                src={buildImageSrc(image)}
                alt={product.title}
                height={292}
                width={292}
                quality={75}
                data-testid="pc-image"
                loading="eager"
                objectFit="cover"
                className="self-center"
              />
            )}
          </div>
          <div className="grid items-start grid-rows-1 gap-4">
            <h3 className="relative mb-0 tra-text-web-heading-3" data-testid="pc-title">
              {product.title}
            </h3>

            <div className="self-end tra-text-web-heading-4">
              <div className="flex items-center gap-x-2">
                {!hasPriceRange &&
                  product.originalPrice &&
                  product.originalPrice > product.price && (
                    <del className="line-through text-neutral-400">
                      <span className="sr-only">
                        {t(
                          'productCard.originalPrice',
                          { price: formatPrice(product.originalPrice!) },
                          { default: 'Original Price {{price}}' },
                        )}
                      </span>
                      {formatPrice(product.originalPrice)}
                    </del>
                  )}
                <p className="mb-0">
                  {hasPriceRange ? (
                    <span>
                      <span className="sr-only">
                        {t(
                          'productCard.priceRange',
                          { min: formatPrice(product.price), max: formatPrice(product.maxPrice!) },
                          { default: 'Price range {{min}} to {{max}}' },
                        )}
                      </span>
                      <span aria-hidden="true">
                        {formatPrice(product.price)} - {formatPrice(product.maxPrice!)}
                      </span>
                    </span>
                  ) : (
                    <>
                      <span className="sr-only">
                        {t(
                          product.originalPrice ? 'productCard.salePrice' : 'productCard.price',
                          { price: formatPrice(product.price) },
                          {
                            default: product.originalPrice
                              ? 'Sale Price {{price}}'
                              : 'Price {{price}}',
                          },
                        )}
                      </span>
                      {formatPrice(product.price)}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Anchor>
  );
};

export default MeaterProductCard;
